import { type ReactNode } from 'react'
import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { BlockerStore } from 'shared/ui/Blocker'
import modalStore from 'shared/ui/Modal/store/modalStore.ts'
import { ModalTypeList } from 'shared/ui/Modal/store/types.ts'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
} from 'entities/SmartCall/api/types'
import { SmartCallApi } from 'entities/SmartCall/api/SmartCallApi.ts'
import { SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'

export class SmartCallEditStore {
  smartCallFlowBuilderStore
  workflow: IFullSmartCallFlowData | null = null
  blockerStore: BlockerStore
  inboxWorkflowEnabled = false
  private _blockerModalId?: string

  constructor(public readonly entities: IEntitiesStore, private id: number) {
    this.smartCallFlowBuilderStore = new SmartCallFlowBuilderStore(this.entities, this)
    makeAutoObservable(this)

    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  private _blockerConfig: {
    title: ReactNode
    primaryText: string
    primaryAction: (blockerModalId: string) => Promise<void>
    modalType: ModalTypeList
    additionalSecondaryActionText?: string
  } = {
    title: '',
    primaryText: '',
    primaryAction: async (blockerModalId: string) => {
      this._blockerModalId = blockerModalId
      await this.onSave({ withError: true, skipPublishChangesModal: true })
    },
    modalType: ModalTypeList.WARNING,
  }

  init = async () => {
    const inbox = await this.entities.inboxesStore.getById(this.id, true)
    if (inbox) this.smartCallFlowBuilderStore.setName(inbox.name)
    await this.initWorkflow()
  }

  private _syncBlockerConfig = () => {
    this._blockerConfig.title = (
      <>
        {this.saveText} call flow
        <br />
        before closing?
      </>
    )
    this._blockerConfig.primaryText = this.saveBtnText
    this._blockerConfig.additionalSecondaryActionText = this.hasActiveCallFlow
      ? 'Don’t publish'
      : 'Don’t save'
  }

  get saveText() {
    return this.hasActiveCallFlow ? 'Publish' : 'Save'
  }

  get saveBtnText() {
    return `${this.saveText} changes`
  }

  get isCreate() {
    return !this.workflow
  }

  get inbox() {
    const inbox = this.entities.inboxesStore.getItem(this.id)
    if (inbox instanceof Inbox) {
      return inbox
    }
    return null
  }

  get params(): IBaseSmartCallFlowData | null {
    return this.smartCallFlowBuilderStore.params
  }

  get hasActiveCallFlow() {
    return this.inboxWorkflowEnabled && this.workflow?.enabled
  }

  get isInvalid() {
    return this.workflow?.is_invalid ?? false
  }

  get isSaveButtonDisabled() {
    if (this.isCreate) return false

    return !this.blockerStore.isExistChanges
  }

  onActivateInboxWorkflow = async (status: boolean) => {
    if (this.workflow) {
      if (this.hasActiveCallFlow) {
        await this._onShowDisableFlowModal()

        return
      }

      await this.updateInboxWorkflow({ id: this.workflow.id, status })
    } else {
      const prevState = this.inboxWorkflowEnabled
      this.inboxWorkflowEnabled = status
      if (status) {
        this.smartCallFlowBuilderStore.buildDefaultWorkflow()
      }
      await this.onSave({})
      if (!this.workflow) {
        this.inboxWorkflowEnabled = prevState
      }
      this._syncBlockerConfig()
    }
  }

  onActivateWorkFlow = async () => {
    if (!this.smartCallFlowBuilderStore.hasActionSteps) {
      toastStore.add({
        type: 'error',
        title: 'Failed to activate',
        desc: 'Add at least one action to the call flow',
      })
      return
    }

    const isValid = this.smartCallFlowBuilderStore.validateForPublish()
    if (!isValid) {
      toastStore.add({
        type: 'error',
        title: 'Failed to activate',
        desc: 'Complete all required fields',
      })

      return
    }

    await this._onShowActivateFlowModal()
  }

  update = (data: IFullSmartCallFlowData) => {
    this.blockerStore.updateCash()
    this.workflow = data
    this._syncBlockerConfig()
  }

  initWorkflow = async () => {
    try {
      const res = await SmartCallApi.getInboxWorkflow(this.id)

      if (res) {
        runInAction(() => {
          this.inboxWorkflowEnabled = res.data.data.active
          this.workflow = res.data.data.workflow
          if (res.data.data.workflow) {
            this.smartCallFlowBuilderStore.loadFromWorkflow(res.data.data.workflow)
          } else {
            this.smartCallFlowBuilderStore.buildDefaultWorkflow()
          }
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._syncBlockerConfig()
      this.blockerStore.updateCash()
    }
  }

  updateInboxWorkflow = async ({ id, status }: { id: string; status: boolean }) => {
    try {
      if (status) {
        await SmartCallApi.enableWorkflow(id)
      } else {
        await SmartCallApi.disableWorkflow(id)
      }

      if (this.workflow) this.update({ ...this.workflow, enabled: status })

      const res = await SmartCallApi.updateInboxWorkflow(this.id, {
        workflow_id: id,
        active: status,
      })

      runInAction(() => {
        this.inboxWorkflowEnabled = res.data.data.active
        this._syncBlockerConfig()
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)
    }
  }

  private _onShowDisableFlowModal = () => {
    return new Promise<void>((resolve) => {
      const modalId = nanoid()
      modalStore.addModal({
        disabledOnAllClose: true,
        id: modalId,
        type: ModalTypeList.ALERT,
        title: 'Disable call flow?',
        desc: 'By disabling it, incoming calls to this inbox will be dropped.',
        primaryAction: {
          text: 'Yes, disable',
          onAction: async () => {
            if (this.workflow)
              await this.updateInboxWorkflow({ id: this.workflow.id, status: false })
            modalStore.removeModal(modalId)
            resolve()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: async () => {
            modalStore.removeModal(modalId)
            resolve()
          },
        },
      })
    })
  }

  private _onShowActivateFlowModal = async () => {
    const modalId = nanoid()
    modalStore.addModal({
      disabledOnAllClose: true,
      id: modalId,
      type: ModalTypeList.INFO,
      title: 'Activate this flow?',
      desc: 'Your call flow is ready. Do you want to activate it now?',
      primaryAction: {
        text: 'Yes, activate',
        onAction: async () => {
          try {
            if (!this.workflow || this.blockerStore.isExistChanges) {
              await this.onSave({ withError: true })
            }

            if (!this.workflow) {
              modalStore.removeModal(modalId)
              return
            }

            await this.updateInboxWorkflow({ id: this.workflow.id, status: true })

            toastStore.add({
              type: 'success',
              title: 'Call flow activated',
            })

            modalStore.removeModal(modalId)
          } catch (e) {
            logger.error(e)

            toastStore.add({
              type: 'error',
              title: 'Something went wrong. Please try again',
            })
          }
        },
      },
      secondaryAction: {
        text: 'No, keep editing',
        onAction: async () => {
          modalStore.removeModal(modalId)
        },
      },
    })
  }

  private _onShowPublishChangesModal = () => {
    return new Promise<void>((resolve, reject) => {
      const modalId = nanoid()
      if (this._blockerModalId) modalStore.setHideModalId(this._blockerModalId)
      modalStore.addModal({
        disabledOnAllClose: true,
        id: modalId,
        type: ModalTypeList.INFO,
        title: 'Publish changes',
        desc: 'The changes will be applied to your active Call Flow',
        primaryAction: {
          text: 'Publish changes',
          onAction: async () => {
            try {
              if (this.workflow && this.params) {
                const res = await SmartCallApi.updateWorkflow(this.workflow.id, this.params)
                this.update(res.data.payload)

                if (!this.inboxWorkflowEnabled) {
                  await this.updateInboxWorkflow({ id: this.workflow.id, status: true })
                }

                toastStore.add({
                  type: 'success',
                  title: 'Changes published',
                })
              }

              modalStore.removeModal(modalId)
              if (this._blockerModalId) {
                modalStore.removeHideModalId(this._blockerModalId)
                modalStore.removeModal(this._blockerModalId)
              }
              resolve()
            } catch (e) {
              logger.error(e)
              if (e instanceof AxiosError) {
                toastStore.add({
                  type: 'error',
                  title: e.errors.first,
                })
              }
              reject(e)
            }
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: async () => {
            if (this._blockerModalId) {
              modalStore.removeHideModalId(this._blockerModalId)
              modalStore.addDisabledRemoveId(this._blockerModalId)
              setTimeout(() => {
                if (this._blockerModalId) modalStore.removeDisabledRemoveId(this._blockerModalId)
              }, 0)
            }
            modalStore.removeModal(modalId)
            reject(new Error('cancelled'))
          },
        },
      })
    })
  }

  onSave = async ({
    withError,
    skipPublishChangesModal,
  }: {
    withError?: boolean
    skipPublishChangesModal?: boolean
  }) => {
    if (!this.params) {
      if (withError) throw new Error('No params')
      return
    }

    if (!this.smartCallFlowBuilderStore.hasActionSteps) {
      toastStore.add({
        type: 'error',
        title: this.hasActiveCallFlow ? 'Failed to publish changes' : 'Failed to save',
        desc: 'Add at least one action to the call flow',
      })

      if (withError) throw new Error('Validation failed')
      return
    }

    const isValid = this.smartCallFlowBuilderStore.validateForPublish()
    if (!isValid) {
      toastStore.add({
        type: 'error',
        title: this.hasActiveCallFlow ? 'Failed to publish changes' : 'Failed to save',
        desc: 'Complete all required fields',
      })

      if (withError) throw new Error('Validation failed')
      return
    }

    try {
      if (this.workflow) {
        if (this.hasActiveCallFlow && !skipPublishChangesModal) {
          await this._onShowPublishChangesModal()

          return
        }

        const res = await SmartCallApi.updateWorkflow(this.workflow.id, this.params)
        if (this.hasActiveCallFlow && skipPublishChangesModal) {
          toastStore.add({
            type: 'success',
            title: 'Changes published',
          })
        } else {
          toastStore.add({
            type: 'info',
            title: 'Changes saved',
          })
        }

        this.update(res.data.payload)
      } else {
        const res = await SmartCallApi.createWorkflow(this.params)

        const { id } = res.data.payload
        await SmartCallApi.createInboxWorkflow(this.id, {
          workflow_id: id,
          active: this.inboxWorkflowEnabled,
        })

        toastStore.add({
          type: 'info',
          title: 'Call flow saved',
        })

        this.update(res.data.payload)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)

      if (withError) {
        throw e
      }
    }
  }
}
